import LightCustomButton from "@components/buttons/LightCustomButton";
import ToggleInput from "@components/inputs/ToggleInput";
import EmptyState from "@components/screens/EmptyState";
import { userManagementApis } from "@services/apis/UserManagement";
import { DeleteWhitelistRequest } from "@services/models/UserManagement/delete-whitelist";
import { MerchantUserData } from "@services/models/UserManagement/get-whitelist-data-user";
import { ToggleWhitelistActivityRequest } from "@services/models/UserManagement/toggle-whitelist-activity";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../../../utils/HandleSuccessErrorSnackbars";

const User = ({ userId }: { userId: number }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [whitelistData, setWhitelistData] = useState<MerchantUserData[]>([]);
  const [deleteLoadingMap, setDeleteLoadingMap] = useState<{
    [key: string]: boolean;
  }>({});
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [errorUser, setErrorUser] = useState(false);
  const [securitySettingToggleOn, setSecuritySettingToggleOn] =
    useState<boolean>(false);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [addressToUpdate, setAddressToUpdate] = useState<string | null>(null);

  const handleDeleteWhitelist = async (
    id: string,
    googleAuthCode?: string,
    emailCode?: string,
  ): Promise<boolean> => {
    const deleteWhitelistRequest: DeleteWhitelistRequest = {
      type: "2",
      id: id,
      userId: userId,
    };

    try {
      setDeleteLoadingMap((prev) => ({
        ...prev,
        [id]: true,
      }));

      const data = await userManagementApis.deleteWhitelist(
        deleteWhitelistRequest,
      );
      handleSuccess({ message: data.msg });
      fetchData({ updateDelete: true });
      return true;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg || "Whitelisting failed. Please try again.";
      handleError({ message: errorMessage });
      return false;
    } finally {
      // Set the specific IP loading state to false
      setDeleteLoadingMap((prev) => ({
        ...prev,
        [id]: false,
      }));
    }
  };

  const handleDelete = (id: string) => {
    handleDeleteWhitelist(id);
  };

  const handleToggleWhitelist = async (
    id: string,
    googleAuthCode?: string,
    emailCode?: string,
  ) => {
    try {
      const toggleWhitelistRequest: ToggleWhitelistActivityRequest = {
        type: "2",
        id: id,
        userId: userId,
      };
      const data = await userManagementApis.toggleWhitelistActivity(
        toggleWhitelistRequest,
      );
      handleSuccess({ message: data.msg });
      fetchData({ updateDelete: true });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating Whitelisting failed. Please try again.";
      handleError({ message: errorMessage });
      return false;
    } finally {
      setAddressToUpdate(null);
    }
  };

  const handleToggle = (id: string) => {
    handleToggleWhitelist(id);
  };

  const fetchData = async ({ updateDelete = false }) => {
    try {
      setLoading(!updateDelete);

      const response = await userManagementApis.getWhitelistDataUser(userId);
      setWhitelistData(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrorUser(true);
    }
  };

  useEffect(() => {
    fetchData({ updateDelete: false });
  }, []);

  return (
    <div className="flex flex-col p-[20px]">
      {!loading ? (
        <div>
          {whitelistData.length !== 0 ? (
            <div className="flex flex-col min-h-[400px] mt-[16px] gap-[20px]">
              <div
                className={`${
                  whitelistData.length > 0 ? "border" : "none"
                } border-border-subtle rounded-lg`}>
                {whitelistData.length > 0 ? (
                  whitelistData[0].users.map((user, index) => (
                    <div
                      key={user.id}
                      className={`flex flex-col space-y-[12px] p-[16px] ${
                        index !== whitelistData[0].users.length - 1
                          ? "border-b border-border-subtle"
                          : ""
                      }`}>
                      <div className="text-text-base text-[14px] font-semibold">
                        {user.tpId}
                      </div>
                      <div className="flex flex-row w-full justify-between items-center">
                        <ToggleInput
                          onToggle={() => {
                            setAddressToDelete(null);
                            handleToggle(user.id.toString());
                          }}
                          isToggled={user.active === 1 ? true : false}
                        />
                        <LightCustomButton
                          text="DELETE"
                          borderColor="border-text-status-red"
                          textColor="text-text-status-red"
                          buttonColor="bg-bg-page-body"
                          onClick={() => {
                            setAddressToUpdate(null);
                            handleDelete(user.id.toString());
                          }}
                          isActive={true}
                          isButtonLoading={deleteLoadingMap[user.id.toString()]}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-text-base text-[14px] font-medium text-center">
                    No Users found for the selected account.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <EmptyState
              text="No Users Added to Whitelist"
              subText="No users have been added to your whitelist yet. Click the button below to start adding addresses to your whitelist."
            />
          )}
        </div>
      ) : (
        <div className="flex flex-col mt-[16px] space-y-4">
          <Skeleton height={44} />
          <Skeleton height={52} />
          <Skeleton height={52} />
        </div>
      )}
    </div>
  );
};

export default User;
